/**
 * The board's pointers: a hand for Select & move, a crosshair for the drawing
 * tools, a no-entry sign for Erase.
 *
 * They are the app's own images rather than the system's, because a recording
 * cannot capture the system pointer: while recording, the board paints its
 * pointer into the canvas — which is what the video is made of — and hides the
 * real one. Using the very same image on screen and in the canvas is what makes
 * the video show exactly what the coach sees, on every computer.
 */

import type { Tool } from './useBoard.ts';

export interface PointerImage {
  /** For CSS `cursor`, with the system's own pointer as the fallback. */
  readonly css: string;
  /** The same image, for painting into the canvas. */
  readonly image: HTMLImageElement | null;
  /** The pixel the pointer points with, from the image's top-left. */
  readonly hotspot: { readonly x: number; readonly y: number };
  readonly size: number;
}

const SIZE = 24;
const INK = '#ffffff';
const EDGE = '#0b1622';

/** An open hand, as a system "grab" pointer: palm, four fingers and a thumb. */
const HAND = `
  <g fill="${INK}" stroke="${EDGE}" stroke-width="1.4" stroke-linejoin="round">
    <rect x="7.2" y="3.2" width="2.6" height="9" rx="1.3"/>
    <rect x="10" y="2.2" width="2.6" height="10" rx="1.3"/>
    <rect x="12.8" y="2.8" width="2.6" height="9.4" rx="1.3"/>
    <rect x="15.6" y="4.4" width="2.6" height="8" rx="1.3"/>
    <rect x="3.6" y="9.6" width="2.6" height="6.6" rx="1.3" transform="rotate(-35 4.9 12.9)"/>
    <path d="M6.8 10.4h11.4v4.6c0 3.6-2.6 6.4-6 6.4h-0.4c-2.8 0-4.4-1.6-5.4-3.6l-0.6-1.4z"/>
  </g>`;

/** A thin crosshair with a gap at the centre, outlined so it reads on any colour. */
const CROSSHAIR = `
  <g stroke-linecap="round">
    <path d="M12 2v7M12 15v7M2 12h7M15 12h7" stroke="${EDGE}" stroke-width="3.4"/>
    <path d="M12 2v7M12 15v7M2 12h7M15 12h7" stroke="${INK}" stroke-width="1.6"/>
  </g>`;

/** A circle with a slash: nothing to pick up here, only drawings to erase. */
const NO_ENTRY = `
  <g fill="none" stroke-linecap="round">
    <circle cx="12" cy="12" r="8" stroke="${EDGE}" stroke-width="4.4"/>
    <path d="M6.6 17.4L17.4 6.6" stroke="${EDGE}" stroke-width="4.4"/>
    <circle cx="12" cy="12" r="8" stroke="${INK}" stroke-width="2.2"/>
    <path d="M6.6 17.4L17.4 6.6" stroke="${INK}" stroke-width="2.2"/>
  </g>`;

function pointer(body: string, hotspot: { x: number; y: number }, fallback: string): PointerImage {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${SIZE}" height="${SIZE}" viewBox="0 0 ${SIZE} ${SIZE}">${body}</svg>`;
  const url = `data:image/svg+xml,${encodeURIComponent(svg)}`;
  // Loaded once, up front, so the first frame of a recording already has it.
  const image = typeof Image === 'undefined' ? null : Object.assign(new Image(SIZE, SIZE), { src: url });
  return { css: `url("${url}") ${hotspot.x} ${hotspot.y}, ${fallback}`, image, hotspot, size: SIZE };
}

const POINTERS = {
  hand: pointer(HAND, { x: 12, y: 12 }, 'grab'),
  crosshair: pointer(CROSSHAIR, { x: 12, y: 12 }, 'crosshair'),
  noEntry: pointer(NO_ENTRY, { x: 12, y: 12 }, 'not-allowed'),
} as const;

/** The pointer for a tool, on screen and in a recording alike. */
export function pointerFor(tool: Tool): PointerImage {
  if (tool === 'select') return POINTERS.hand;
  if (tool === 'erase') return POINTERS.noEntry;
  return POINTERS.crosshair;
}
